import { useCallback, useContext, useMemo } from 'react';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import useLeadStagesCopy from 'hooks/useLeadStagesCopy';
import { confirmStopLeadsOrder } from 'modules/releaseOrderConfirm';
import { getIsArrayEmpty } from 'utils';

// Asks before a revoke takes away the file a lead's "One at a time" order waits on.
export default function useConfirmRevokeInOrder() {
  const {
    dataRoom: { _id: dataRoomId, dealRoom },
  } = useContext(DataRoomContext);

  const { getCopies } = useLeadStagesCopy();

  const stageCopies = useMemo(
    () => getCopies(dealRoom?.leadStagesCopies),
    [dealRoom?.leadStagesCopies, getCopies],
  );

  const confirmRevoke = useCallback(
    async (dataRoomNodeIds: string[], dataRoomMemberIds: string[]) => {
      if (getIsArrayEmpty(dataRoomMemberIds)) return true;

      const { data } = await dataRoomApi.nodes.previewReleaseOrderShare(
        dataRoomNodeIds,
        {
          changedPermissions: dataRoomMemberIds.map((dataRoomMemberId) => ({
            dataRoomMemberId,
            enableView: false,
          })),
        },
      );

      return confirmStopLeadsOrder(data, stageCopies);
    },
    [stageCopies],
  );

  // Folders expand on the BE, so the root nodes cover the whole Data Room.
  const confirmRevokeAll = useCallback(
    async (dataRoomMemberIds: string[]) => {
      if (getIsArrayEmpty(dataRoomMemberIds)) return true;

      const { data } = await dataRoomApi.nodes.getChildren(dataRoomId);

      return confirmRevoke(
        data.map(({ _id }) => _id),
        dataRoomMemberIds,
      );
    },
    [confirmRevoke, dataRoomId],
  );

  return { confirmRevoke, confirmRevokeAll };
}
